import React, { useEffect, useRef, useState } from 'react';
import { X, Send, MessageSquare } from 'lucide-react';
import { firmMessageThread, sendFirmMessage, uploadMessageAttachment } from '../../lib/db';
import { supabase } from '../../services/supabaseClient';
import toast from '../../lib/toast';
import MessageAttachments, { PendingAttachments, AttachButton } from './MessageAttachments';
import RichText from './RichText';

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function nameOf(u) {
  if (!u) return 'Unknown';
  return [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || 'User';
}

/**
 * Side panel showing a single message and all its threaded replies.
 *
 * Props:
 *   parent     — the parent firm_messages row (with sender info attached)
 *   firmId     — caller's firm
 *   channelId  — channel the parent lives in
 *   userId     — caller's id
 *   onClose    — () => void
 *   onChange   — optional callback after a reply is sent so the parent can
 *                refresh thread counts
 */
export default function ThreadPanel({ parent, firmId, channelId, userId, userProfile, onClose, onChange }) {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body,    setBody]    = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!parent?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    firmMessageThread(parent.id).then(({ data }) => {
      if (cancelled) return;
      setReplies(Array.isArray(data) ? data : []);
      setLoading(false);
    });

    // Realtime — pick up replies inserted after we opened the panel.
    const sub = supabase
      .channel(`thread_${parent.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'firm_messages',
        filter: `parent_message_id=eq.${parent.id}`,
      }, async (payload) => {
        if (cancelled) return;
        const r = payload.new;
        if (r.sender_id === userId) return; // optimistic added already
        const { data: sender } = await supabase
          .from('users')
          .select('id, first_name, last_name, email')
          .eq('id', r.sender_id)
          .maybeSingle();
        if (cancelled) return;
        setReplies((prev) => prev.some((p) => p.id === r.id)
          ? prev
          : [...prev, { ...r, first_name: sender?.first_name, last_name: sender?.last_name, email: sender?.email }]);
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'firm_messages',
        filter: `parent_message_id=eq.${parent.id}`,
      }, (payload) => {
        if (cancelled) return;
        const r = payload.new;
        setReplies((prev) => prev.map((p) => p.id === r.id
          ? { ...p, body: r.body, attachments: r.attachments, deleted_at: r.deleted_at, edited_at: r.edited_at }
          : p));
      })
      .subscribe();

    return () => { cancelled = true; supabase.removeChannel(sub); };
  }, [parent?.id, userId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [replies]);

  const send = async (e) => {
    e.preventDefault();
    const text = body.trim();
    const files = pendingFiles;
    if (!text && files.length === 0) return;
    setSending(true);

    const msgId = (window.crypto?.randomUUID && window.crypto.randomUUID())
      || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const optimistic = {
      id: msgId, body: text || null, sender_id: userId, parent_message_id: parent.id,
      created_at: new Date().toISOString(), attachments: [], _optimistic: true,
      first_name: userProfile?.first_name, last_name: userProfile?.last_name, email: userProfile?.email,
    };
    setReplies((prev) => [...prev, optimistic]);
    setBody('');
    setPendingFiles([]);

    const attachments = [];
    for (const file of files) {
      const { data: a, error: upErr } = await uploadMessageAttachment(
        'firm',
        { firmId, channelId, messageId: msgId },
        file,
      );
      if (upErr) {
        toast.error(`Upload failed (${file.name}): ${upErr.message}`);
        setReplies((prev) => prev.filter((m) => m.id !== msgId));
        setSending(false);
        setBody(text); setPendingFiles(files);
        return;
      }
      attachments.push(a);
    }

    const { data, error } = await sendFirmMessage(firmId, channelId, userId, {
      id:          msgId,
      body:        text || null,
      attachments,
      parent_message_id: parent.id,
    });
    setSending(false);
    if (error) {
      toast.error('Failed to send: ' + error.message);
      setReplies((prev) => prev.filter((m) => m.id !== msgId));
      setBody(text); setPendingFiles(files);
    } else {
      setReplies((prev) => prev.map((m) => m.id === msgId ? { ...data, first_name: userProfile?.first_name, last_name: userProfile?.last_name, email: userProfile?.email } : m));
      onChange?.();
    }
  };

  if (!parent) return null;

  return (
    // Mobile: take full screen as an overlay (the w-96 = 384px panel
    // is wider than a 375px iPhone, so without responsive sizing it
    // pushed the message list off-screen). lg+: stays the designed
    // 384px side panel.
    <div className="absolute inset-0 z-20 w-full lg:static lg:inset-auto lg:z-auto lg:flex-shrink-0 lg:w-96 border-l border-slate-100 dark:border-slate-800 flex flex-col bg-white dark:bg-slate-900">
      <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <MessageSquare size={14} className="text-indigo-500 flex-shrink-0" />
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Thread</span>
          <span className="text-xs text-slate-400">· {replies.length} repl{replies.length === 1 ? 'y' : 'ies'}</span>
        </div>
        <button onClick={onClose} className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
          <X size={14} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Parent message */}
        <div className="rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-sm font-semibold text-slate-900 dark:text-white">{nameOf(parent.sender)}</span>
            <span className="text-[11px] text-slate-400">{formatTime(parent.created_at)}</span>
          </div>
          {parent.deleted_at ? (
            <p className="text-xs italic text-slate-400">Message deleted</p>
          ) : (
            <>
              <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words">
                <RichText body={parent.body} currentUserId={userId} />
              </p>
              <MessageAttachments attachments={parent.attachments} />
            </>
          )}
        </div>

        <div className="text-[11px] text-slate-400 px-1">Replies</div>

        {loading && (
          <p className="text-sm text-slate-500 text-center py-4">Loading…</p>
        )}

        {!loading && replies.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-4">Be the first to reply.</p>
        )}

        {replies.map((r) => (
          <div key={r.id} className="flex gap-2">
            <div className="w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-[10px] font-semibold text-slate-600 dark:text-slate-300 flex-shrink-0">
              {((r.first_name || r.email || '?').charAt(0) + (r.last_name?.charAt(0) || '')).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-semibold text-slate-900 dark:text-white">
                  {r.sender_id === userId ? 'You' : nameOf(r)}
                </span>
                <span className="text-[11px] text-slate-400">{formatTime(r.created_at)}</span>
                {r.edited_at && !r.deleted_at && <span className="text-[11px] text-slate-400 italic">(edited)</span>}
              </div>
              {r.deleted_at ? (
                <p className="text-xs italic text-slate-400">Message deleted</p>
              ) : (
                <>
                  <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words">
                    <RichText body={r.body} currentUserId={userId} />
                  </p>
                  <MessageAttachments attachments={r.attachments} />
                </>
              )}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="border-t border-slate-100 dark:border-slate-800 p-3">
        <PendingAttachments
          pending={pendingFiles.map((f) => ({ file: f, mime: f.type }))}
          onRemove={(idx) => setPendingFiles((cur) => cur.filter((_, i) => i !== idx))}
        />
        <div className="flex items-end gap-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-1.5">
          <AttachButton
            disabled={sending}
            onSelect={(files) => setPendingFiles((cur) => [...cur, ...files])}
          />
          <input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Reply to thread…"
            className="flex-1 bg-transparent text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none py-1"
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(e); } }}
          />
          <button
            type="submit"
            disabled={(!body.trim() && pendingFiles.length === 0) || sending}
            className="flex-shrink-0 p-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white transition"
          >
            <Send size={13} />
          </button>
        </div>
      </form>
    </div>
  );
}
